/**
 * Width-aware layout rules for ECharts options. Pure functions — no DOM,
 * no token reads — so every rule is unit-testable in node, the same way
 * `pie-base.ts` is.
 *
 * ECharts' `autoresize` (on every chart) only rescales the canvas;
 * margins, label policy, legend layout and tooltip bounds were fixed px
 * written once into the option. These helpers derive them from the
 * chart's measured width instead. Width is quantised upstream
 * (`useChartLayout`, 16px steps) because vue-echarts replaces the whole
 * option on every rebuild, resetting legend toggles and zoom.
 *
 * Conventions:
 *   - `width <= 0` means "not measured yet": every helper returns the
 *     legacy output so first paint and tests are unchanged.
 *   - Print never gets interactive affordances (scroll legend, zoom,
 *     roam). Print can still be narrow — a chart in a PDF column cell is
 *     ~160–330px — so pure layout rules (label widths, compact ticks)
 *     do apply there.
 */

import { formatValue, formatValueCompact } from "../format.ts";

/** Below this the chart is "narrow": tighter gaps, smaller caps. */
export const NARROW = 480;
/** Below this the chart is "compact". */
export const COMPACT = 640;
/** Width quantisation step for chart rebuilds, px. */
export const WIDTH_STEP = 16;

/** Approx px per character of the 10–11px mono chart face. Errs wide. */
export const charPx = (fontPx = 11): number => 0.62 * fontPx;

export interface LayoutCtx {
  /** Measured chart width, px (0 = unmeasured). */
  width: number;
  print: boolean;
  /** Coarse pointer (touch) and not print. */
  coarse: boolean;
}

/** Rough plot width once the value-axis labels and margins are taken. */
export const plotWidthEstimate = (width: number): number =>
  width > 0 ? Math.max(0, width - 72) : 0;

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

const maxChars = (labels: ReadonlyArray<unknown>): number =>
  labels.reduce<number>((m, l) => Math.max(m, String(l ?? "").length), 0);

// ─── Legend ────────────────────────────────────────────────────────────

export const LEGEND_TOP = 10;
export const LEGEND_PLOT_GAP = 14;
/** Height of one legend row's text/marker, px. */
export const LEGEND_ROW_TEXT = 14;

/** Rendered width of one legend item: marker + gap + text + item gap. */
export const legendItemWidth = (name: string, itemGap = 18): number =>
  10 + 5 + name.length * 6.5 + itemGap;

/** How many rows a plain (wrapping) legend of `names` needs at `width`. */
export function estimateLegendRows(
  names: ReadonlyArray<string>,
  width: number,
  itemGap = 18,
): number {
  let rows = 1;
  let x = 0;
  for (const n of names) {
    const w = legendItemWidth(n, itemGap);
    if (x > 0 && x + w > width) {
      rows++;
      x = 0;
    }
    x += w;
  }
  return rows;
}

export interface LegendLayout {
  type: "plain" | "scroll";
  itemGap: number;
  rows: number;
  /** `grid.top` that clears a top legend of `rows` rows. */
  reserveTop: number;
}

/** Most rows a top legend may wrap to on screen before it scrolls. */
const SCREEN_LEGEND_ROWS = { narrow: 2, wide: 1 } as const;

/**
 * Share of the legend's width the row estimate may fill. The estimate is
 * a fixed 6.5px per character, and an embedder's `--lucent-chart-font-family`
 * or wide glyphs can render wider; an underestimate means a `plain` legend
 * wraps onto a row nobody reserved, over the plot. The headroom absorbs
 * ~15% of error at the cost of sometimes reserving a row that stays empty.
 */
export const LEGEND_FIT_HEADROOM = 0.85;

/**
 * Top-legend layout. Wide screens keep the single scroll row; narrow
 * screens may wrap to two rows (a scroll row there shows about one
 * series per page) before falling back to scroll; print always wraps,
 * and — new — reserves every row, so a wrapped PDF legend no longer
 * runs into the plot. One row reserves exactly 38px, the old fixed
 * `grid.top`.
 */
export function legendLayout(
  names: ReadonlyArray<string>,
  ctx: Pick<LayoutCtx, "width" | "print">,
): LegendLayout {
  const narrow = ctx.width > 0 && ctx.width < NARROW;
  const itemGap = narrow ? 12 : 18;
  const reserve = (rows: number) =>
    LEGEND_TOP + rows * (LEGEND_ROW_TEXT + itemGap) - itemGap + LEGEND_PLOT_GAP;
  if (ctx.width <= 0 || names.length <= 1) {
    return { type: ctx.print ? "plain" : "scroll", itemGap: 18, rows: 1, reserveTop: reserve(1) };
  }
  const rows = estimateLegendRows(names, (ctx.width - 16) * LEGEND_FIT_HEADROOM, itemGap);
  if (ctx.print) return { type: "plain", itemGap, rows, reserveTop: reserve(rows) };
  const cap = narrow ? SCREEN_LEGEND_ROWS.narrow : SCREEN_LEGEND_ROWS.wide;
  if (rows <= cap) return { type: "plain", itemGap, rows, reserveTop: reserve(rows) };
  return { type: "scroll", itemGap, rows: 1, reserveTop: reserve(1) };
}

// ─── Category axis labels ──────────────────────────────────────────────

/**
 * How a category axis copes when its labels don't fit:
 *   - `thin`:   let ECharts drop labels (time-like axes: every label
 *               isn't needed, rotation reads badly).
 *   - `rotate`: show every label at 45°, truncated; thin only when even
 *               rotated labels can't fit.
 *   - `all`:    every label matters (waterfall steps) — 90° at 10px,
 *               thinning only as a last resort.
 */
export type CategoryLabelMode = "thin" | "rotate" | "all";

export interface CategoryLabelInput {
  labels: ReadonlyArray<unknown>;
  /** Plot width, px — `plotWidthEstimate(width)`. */
  plotWidth: number;
  mode: CategoryLabelMode;
  fontPx?: number;
}

/** `axisLabel` fields for a horizontal category axis. `{}` = defaults. */
export function categoryLabelPolicy(input: CategoryLabelInput): Record<string, unknown> {
  const n = input.labels.length;
  if (input.plotWidth <= 0 || n === 0) return {};
  const slot = input.plotWidth / n;
  const natural = maxChars(input.labels) * charPx(input.fontPx);
  if (natural + 8 <= slot) return {};
  if (input.mode === "thin") return { hideOverlap: true };
  const cap = input.plotWidth < NARROW ? 72 : 96;
  const width = Math.max(24, Math.min(natural, cap));
  if (input.mode === "all" && slot >= 12) {
    return { interval: 0, rotate: 90, fontSize: 10, width, overflow: "truncate" };
  }
  if (slot >= 14) {
    return { interval: 0, rotate: 45, width, overflow: "truncate" };
  }
  return { rotate: 45, width, overflow: "truncate", hideOverlap: true };
}

export const CATEGORY_LABEL_MIN_PX = 48;
export const CATEGORY_LABEL_MAX_PX = 160;

/**
 * Width for labels on a vertical category axis (horizontal bars,
 * heatmap rows, …): the longest label, capped at 30% of the chart so a
 * long name can't squeeze the plot to nothing. 160px at 680, ~96px at
 * 320. Unmeasured → the 160px cap.
 */
export function categoryLabelWidth(
  labels: ReadonlyArray<unknown>,
  width: number,
  fontPx = 11,
): number {
  const natural = Math.ceil(maxChars(labels) * charPx(fontPx));
  const cap =
    width > 0
      ? clamp(Math.round(width * 0.3), CATEGORY_LABEL_MIN_PX, CATEGORY_LABEL_MAX_PX)
      : CATEGORY_LABEL_MAX_PX;
  return Math.max(1, Math.min(natural, cap));
}

/** `axisLabel` fields for a vertical (y-side) category axis. */
export const verticalCategoryLabel = (
  labels: ReadonlyArray<unknown>,
  width: number,
): { width: number; overflow: "truncate" } => ({
  width: categoryLabelWidth(labels, width),
  overflow: "truncate",
});

// ─── Value axis ticks ──────────────────────────────────────────────────

/** Plot narrower than this gets compact tick labels ("1.2M"). */
export const COMPACT_TICKS_BELOW = 480;

/**
 * Value-axis tick formatter. On a narrow plot, numbers ≥ 1000 render
 * compact (`$1.2M` rather than `$1,200,000`) so the labels don't eat the
 * plot. Small numbers keep the full format — compacting 0.25 would
 * round it to "0.3".
 */
export function axisTickFormatter(
  format: string | undefined,
  plotWidth: number,
): ((v: number | string) => string) | undefined {
  const full = format ? (v: number | string) => formatValue(v, format) : undefined;
  if (!(plotWidth > 0 && plotWidth < COMPACT_TICKS_BELOW)) return full;
  return (v: number | string) =>
    typeof v === "number" && Math.abs(v) >= 1000
      ? formatValueCompact(v, format)
      : full
        ? full(v)
        : String(v);
}

// ─── dataZoom ──────────────────────────────────────────────────────────

/** `true` (accepted by the spec schema) means a slider, as it always has. */
export type DataZoomMode = "slider" | "inside" | "both" | boolean | undefined;

export interface DataZoomLayout {
  dataZoom?: Array<Record<string, unknown>>;
  /** Extra `grid.bottom` the slider needs, px. */
  reserveBottom: number;
}

export const SLIDER_HEIGHT = 20;
export const SLIDER_HEIGHT_COARSE = 24;
export const SLIDER_GAP = 8;

/**
 * dataZoom for a cartesian x axis.
 *   - Print: none — a static PDF can't zoom, and the slider is noise.
 *   - Touch: `inside` becomes a slider. ECharts' inside zoom captures
 *     one-finger drag (and turning that off disables pinch too), which
 *     traps page scrolling on phones; a slider can't.
 */
export function dataZoomOption(
  mode: DataZoomMode,
  ctx: Pick<LayoutCtx, "print" | "coarse">,
  extra: Record<string, unknown> = {},
): DataZoomLayout {
  if (!mode || ctx.print) return { reserveBottom: 0 };
  const height = ctx.coarse ? SLIDER_HEIGHT_COARSE : SLIDER_HEIGHT;
  const slider = {
    type: "slider",
    height,
    bottom: 4,
    brushSelect: !ctx.coarse,
    ...(ctx.coarse ? { handleSize: "120%" } : {}),
    ...extra,
  };
  const inside = { type: "inside", ...extra };
  const useSlider = mode === true || mode === "slider" || mode === "both" || ctx.coarse;
  const useInside = !ctx.coarse && (mode === "inside" || mode === "both");
  const dataZoom = [...(useInside ? [inside] : []), ...(useSlider ? [slider] : [])];
  return { dataZoom, reserveBottom: useSlider ? height + SLIDER_GAP : 0 };
}

// ─── Tooltip ───────────────────────────────────────────────────────────

/**
 * Tooltip bounds. `confine` keeps the tooltip inside the chart box (it
 * otherwise runs off a phone screen); the CSS cap makes long content
 * wrap rather than widen past the chart.
 */
export function tooltipBounds(width: number): Record<string, unknown> {
  return {
    confine: true,
    ...(width > 0
      ? {
          extraCssText: `max-width:${Math.max(160, width - 16)}px;white-space:normal;overflow-wrap:anywhere;`,
        }
      : {}),
  };
}
